import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';

export interface PracticesPage {
  success: true;
  data: Practice[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export interface Practice {
  id: string;
  name: string;
  slug: string;
  /** Industry the practice operates in — agency-set. Defaults to 'dental'. */
  vertical?: string;
  specialty?: string;
  phone?: string;
  website?: string;
  status: 'active' | 'inactive' | 'trial' | 'draft' | 'invited' | 'onboarding' | 'suspended';
  locationsCount: number;
  usersCount: number;
  createdAt: string;
  onboardingComplete: boolean;
  pendingInvitation?: Invitation | null;
  // Command-center declutter flag. Non-null = snoozed (hidden from the switcher).
  snoozedAt?: string | null;
  // Per-practice feature flags, toggled from the Command Center Admin tab.
  featureFlags?: Record<string, boolean> | null;
}

export type PipelineStageStatus = 'pending' | 'in_progress' | 'completed' | 'failed';
export type PipelineStatus = Partial<
  Record<'crawl' | 'extraction' | 'audit' | 'competitors', PipelineStageStatus>
>;

export interface Location {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  website?: string;
  isDefault: boolean;
  campaignApprovalPreference?: 'autopilot' | 'guided';
  contentApprovalPreference?: 'auto' | 'review';
  reviewSettings?: { mode: 'autopilot' | 'guided' | null } | null;
  anthropicModels?: Record<string, string> | null;
  pipelineStatus?: PipelineStatus | null;
}

export interface ReviewSmsStatus {
  status: 'not_started' | 'setup' | 'awaiting_registration' | 'live' | 'paused';
  pausedReason: string | null;
  test: { enabled: boolean; phoneMasked: string | null; usesSharedNumber: boolean };
  sendingNumber: { masked: string | null; provisioningState: string };
  registration: { approved: boolean; brandState: string; campaignState: string };
  liveSince: string | null;
  /** Whether anything can text yet: number live and 10DLC approved (Settings → Communication → Texting). */
  texting?: 'ready' | 'setup' | 'awaiting_registration';
}

export interface PromptDescriptor {
  name: string;
  group: string;
  label: string;
  /** Agent skills run in agent-suite on Claude only; backend prompts may also use OpenRouter. */
  claudeOnly?: boolean;
}

export interface PromptRegistryModel {
  id: string;
  label: string;
  provider: 'anthropic' | 'openrouter';
}

export interface PromptRegistry {
  prompts: PromptDescriptor[];
  models: PromptRegistryModel[];
  /** Off: OpenRouter picks are kept but each prompt runs on its Claude default. */
  openRouterEnabled: boolean;
  defaultModel: string;
}

export interface Invitation {
  id: string;
  email: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  sentAt: string;
  expiresAt: string;
}

export interface CreatePracticePayload {
  name: string;
  website?: string;
  vertical?: string;
  specialty?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  contact: { name: string; email: string; phone?: string };
  sendInvitation?: boolean;
}

export interface AgencyStats {
  totalPractices: number;
  activePractices: number;
  trialPractices: number;
  totalLocations: number;
  totalUsers: number;
}

export interface PracticeUser {
  user: {
    id: string;
    firstName: string;
    lastName?: string;
    email: string;
    lastLoginAt?: string | null;
  };
}

export interface CrawlProgressStep {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'done' | 'failed' | 'skipped';
  error?: string;
  meta?: Record<string, unknown>;
}
export interface CrawlMeta {
  pageCount: number;
  urls: string[];
  jsRendered?: boolean;
  progress?: CrawlProgressStep[];
  errorMessage?: string | null;
}
export interface ExtractionMeta {
  practiceName: string | null;
  phones: string[];
  addresses: string[];
  structuredAddress: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    full: string;
  } | null;
  services: string[];
  providers: string[];
  technology: string[];
  insurances: string[];
  languages: string[];
  membershipPlans: string[];
  paymentOptions: string[];
  hasOfficeHours: boolean;
}
export interface AuditMeta {
  overallScore: number | null;
  grade: string | null;
  summary: string | null;
  progress?: CrawlProgressStep[];
}
export interface CompetitorsMeta {
  count: number;
}

export interface PipelineStepMeta {
  crawl?: CrawlMeta;
  extraction?: ExtractionMeta;
  audit?: AuditMeta;
  competitors?: CompetitorsMeta;
}

export interface PipelineDetail {
  name: string;
  hasWebsite: boolean;
  website: string | null;
  status: PipelineStatus;
  errors: Record<string, string>;
  meta: PipelineStepMeta;
}

export interface PracticeDetail extends Practice {
  locations: Location[];
  createdByUser?: { id: string; firstName: string; lastName?: string; email: string } | null;
  users: PracticeUser[];
  invitations: Invitation[];
}

/**
 * What an agency-triggered action actually did.
 *
 *   'queued' — a background job was enqueued; it has not run yet.
 *   'done'   — it finished, and changed something.
 *   'noop'   — it finished and deliberately changed nothing (a precondition
 *              was missing, or there was nothing new to generate).
 *
 * All three used to render as "completed", which is what made a queued job and
 * a declined one look identical to a success.
 */
export type AiActionOutcome = 'queued' | 'done' | 'noop';

export interface AiActionResult {
  outcome: AiActionOutcome;
  message: string;
  detail?: Record<string, unknown>;
}

/** Latest SiteAnalysis for a location — null when the site has never been crawled. */
export interface SiteAuditStatus {
  id: string;
  status: string;
  pagesFetched: number | null;
  createdAt: string;
  updatedAt: string;
  errorMessage: string | null;
}

export const agencyApi = {
  getPractices: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }): Promise<PracticesPage> => {
    const { data } = await apiClient.get<PracticesPage>('/agency/practices', { params });
    return data;
  },

  getPractice: async (id: string): Promise<ApiResponse<PracticeDetail>> => {
    const { data } = await apiClient.get<ApiResponse<PracticeDetail>>(`/agency/practices/${id}`);
    return data;
  },

  createPractice: async (payload: CreatePracticePayload): Promise<ApiResponse<Practice>> => {
    const { data } = await apiClient.post<ApiResponse<Practice>>('/agency/practices', payload);
    return data;
  },

  updatePractice: async (
    id: string,
    payload: {
      name?: string;
      vertical?: string;
      specialty?: string;
      phone?: string;
      website?: string;
      status?: 'active' | 'inactive';
    },
  ): Promise<ApiResponse<Practice>> => {
    const { data } = await apiClient.patch<ApiResponse<Practice>>(
      `/agency/practices/${id}`,
      payload,
    );
    return data;
  },

  /** The text review program of one location (PRD 5 §4.7): status pill on the Reviews module. */
  getReviewSmsStatus: async (practiceId: string, locationId: string): Promise<ReviewSmsStatus> => {
    const { data } = await apiClient.get<ApiResponse<ReviewSmsStatus>>(
      `/agency/practices/${practiceId}/locations/${locationId}/review-sms/status`,
    );
    return data.data;
  },

  updatePracticeFlags: async (
    id: string,
    flags: Record<string, boolean>,
  ): Promise<ApiResponse<{ id: string; featureFlags: Record<string, boolean> }>> => {
    const { data } = await apiClient.patch<
      ApiResponse<{ id: string; featureFlags: Record<string, boolean> }>
    >(`/agency/practices/${id}/feature-flags`, { flags });
    return data;
  },

  deletePractice: async (id: string): Promise<void> => {
    await apiClient.delete(`/agency/practices/${id}`);
  },

  addLocation: async (
    practiceId: string,
    location: Omit<Location, 'id'>,
  ): Promise<ApiResponse<Location>> => {
    const { data } = await apiClient.post<ApiResponse<Location>>(
      `/agency/practices/${practiceId}/locations`,
      location,
    );
    return data;
  },

  updateLocation: async (
    practiceId: string,
    locationId: string,
    updates: Partial<Location>,
  ): Promise<ApiResponse<Location>> => {
    const { data } = await apiClient.patch<ApiResponse<Location>>(
      `/agency/practices/${practiceId}/locations/${locationId}`,
      updates,
    );
    return data;
  },

  deleteLocation: async (practiceId: string, locationId: string): Promise<void> => {
    await apiClient.delete(`/agency/practices/${practiceId}/locations/${locationId}`);
  },

  setReviewMode: async (
    practiceId: string,
    locationId: string,
    mode: 'autopilot' | 'guided' | null,
  ): Promise<void> => {
    await apiClient.patch(`/agency/practices/${practiceId}/locations/${locationId}/review-mode`, {
      mode,
    });
  },

  sendInvitation: async (
    practiceId: string,
    email: string,
    contactName: string,
    role: string,
    force?: boolean,
  ): Promise<ApiResponse<{ message: string }>> => {
    const { data } = await apiClient.post<ApiResponse<{ message: string }>>(
      `/agency/practices/${practiceId}/invite`,
      { email, contactName, role, force },
    );
    return data;
  },

  checkInviteConflict: async (
    email: string,
  ): Promise<{ hasConflict: boolean; existingPractices: Array<{ id: string; name: string }> }> => {
    const { data } = await apiClient.get<
      ApiResponse<{ hasConflict: boolean; existingPractices: Array<{ id: string; name: string }> }>
    >('/agency/check-invite-conflict', { params: { email } });
    return data.data;
  },

  revokeInvitation: async (practiceId: string, invitationId: string): Promise<void> => {
    await apiClient.delete(`/agency/practices/${practiceId}/invitations/${invitationId}`);
  },

  resendInvitation: async (
    practiceId: string,
    invitationId: string,
    force?: boolean,
  ): Promise<ApiResponse<{ message: string }>> => {
    const { data } = await apiClient.post<ApiResponse<{ message: string }>>(
      `/agency/practices/${practiceId}/invitations/${invitationId}/resend`,
      { force },
    );
    return data;
  },

  /** Pushes a pending invitation's expiry out by 15 days; the existing link keeps working. */
  extendInvitation: async (
    practiceId: string,
    invitationId: string,
  ): Promise<ApiResponse<Pick<Invitation, 'id' | 'status' | 'expiresAt'>>> => {
    const { data } = await apiClient.post<
      ApiResponse<Pick<Invitation, 'id' | 'status' | 'expiresAt'>>
    >(`/agency/practices/${practiceId}/invitations/${invitationId}/extend`);
    return data;
  },

  removeTeamMember: async (practiceId: string, userId: string): Promise<void> => {
    await apiClient.delete(`/agency/practices/${practiceId}/users/${userId}`);
  },

  getStats: async (): Promise<ApiResponse<AgencyStats>> => {
    const { data } = await apiClient.get<ApiResponse<AgencyStats>>('/agency/stats');
    return data;
  },

  /** The signed-in admin's pinned practices, in pin order. Per user, never per agency. */
  getPinnedPractices: async (): Promise<string[]> => {
    const { data } = await apiClient.get<ApiResponse<string[]>>('/agency/me/pinned-practices');
    return data.data;
  },

  /** Replace the pins with exactly this list (at most PINNED_PRACTICES_MAX). */
  setPinnedPractices: async (practiceIds: string[]): Promise<string[]> => {
    const { data } = await apiClient.put<ApiResponse<string[]>>('/agency/me/pinned-practices', {
      practiceIds,
    });
    return data.data;
  },

  getPromptRegistry: async (): Promise<ApiResponse<PromptRegistry>> => {
    const { data } = await apiClient.get<ApiResponse<PromptRegistry>>('/agency/prompt-registry');
    return data;
  },

  resetSafetyExperience: async (
    practiceId: string,
    locationId: string,
  ): Promise<ApiResponse<{ completions: number; equipment: number }>> => {
    const { data } = await apiClient.post<ApiResponse<{ completions: number; equipment: number }>>(
      `/agency/practices/${practiceId}/locations/${locationId}/safety/reset`,
    );
    return data;
  },

  resetReviewAccelerator: async (
    practiceId: string,
    locationId: string,
  ): Promise<ApiResponse<{ invites: number; funnelEvents: number; submissions: number }>> => {
    const { data } = await apiClient.post<
      ApiResponse<{ invites: number; funnelEvents: number; submissions: number }>
    >(`/agency/practices/${practiceId}/locations/${locationId}/review-accelerator/reset`);
    return data;
  },

  runAiAction: async (
    practiceId: string,
    action: string,
    locationId: string,
  ): Promise<AiActionResult> => {
    const { data } = await apiClient.post<ApiResponse<AiActionResult>>(
      `/agency/practices/${practiceId}/ai-actions/${action}`,
      { locationId },
    );
    return data.data;
  },

  getAuditStatus: async (
    practiceId: string,
    locationId: string,
  ): Promise<SiteAuditStatus | null> => {
    const { data } = await apiClient.get<ApiResponse<SiteAuditStatus | null>>(
      `/agency/practices/${practiceId}/locations/${locationId}/audit-status`,
    );
    return data.data;
  },

  getLocationPipeline: async (
    practiceId: string,
    locationId: string,
  ): Promise<ApiResponse<PipelineDetail>> => {
    const { data } = await apiClient.get<ApiResponse<PipelineDetail>>(
      `/agency/practices/${practiceId}/locations/${locationId}/pipeline`,
    );
    return data;
  },

  startOnboarding: async (
    practiceId: string,
    locationId: string,
    mode: 'start' | 'retry' | 'restart' | 'run_stage' | 'cancel',
    stage?: 'crawl' | 'extraction' | 'competitors' | 'audit',
  ): Promise<ApiResponse<{ message: string; pipelineStatus?: Record<string, string> }>> => {
    const { data } = await apiClient.post<
      ApiResponse<{ message: string; pipelineStatus?: Record<string, string> }>
    >(`/agency/practices/${practiceId}/locations/${locationId}/pipeline/start`, {
      mode,
      ...(stage && { stage }),
    });
    return data;
  },
};
